import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import type { FC } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useAreaFormContext } from '../AreaFormContext'
import { AreaFormValues } from '../AreaFormContext/useAreaForm'
import { NumberInput } from './NumberInput'

type DurationFieldConfig = {
  durationName:
    | 'expirationDuration.years'
    | 'expirationDuration.months'
    | 'expirationDuration.days'
    | 'expirationDuration.hours'
  formatName:
    | 'expirationYear'
    | 'expirationMonth'
    | 'expirationDay'
    | 'expirationHour'
  label: string
  min: number
  max: number
  formats: readonly string[]
  formatLabels: Record<string, string>
}

type Props = {
  config: DurationFieldConfig
}

export const DurationField: FC<Props> = ({ config }) => {
  const { control, setValue } = useAreaFormContext()
  const formatValue = useWatch<AreaFormValues>({
    control,
    name: config.formatName,
  })

  return (
    <Controller
      name={config.durationName}
      control={control}
      rules={{
        min: {
          value: config.min,
          message: `${config.min}以上の値を入力してください`,
        },
        max: {
          value: config.max,
          message: `${config.max}以下の値を入力してください`,
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} required sx={{ gap: 0.5 }}>
          <Typography
            component="label"
            sx={{ color: 'text.primary', fontSize: 14, fontWeight: 'bold' }}
          >
            {config.label}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '100px 1fr',
              gap: 2,
            }}
          >
            <NumberInput
              value={field.value}
              min={config.min}
              error={!!error}
              onChange={field.onChange}
            />
            <Select
              value={formatValue}
              onChange={e =>
                setValue(config.formatName, e.target.value as never)
              }
              required
              sx={{ flex: 1, backgroundColor: 'white' }}
            >
              {config.formats.map(v => (
                <MenuItem key={v} value={v}>
                  {config.formatLabels[v]}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {error && (
            <FormHelperText sx={{ margin: 0 }}>{error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}
